import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  Button, 
  Typography, 
  Grid, 
  Box, 
  Chip, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  FormControl, 
  Checkbox, 
  MenuItem, 
  ListItemText, 
  OutlinedInput, 
  InputLabel, 
  Select
} from '@mui/material';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import translations from '../../translations/AddRoleFormTranslation';
import permissionsTranslations from '../../translations/permissionsTranslations';
import PermissionsStyles from './styles/PermissionsStyles';

const AddRoleForm = ({ onAddRole, onCancel, pages }) => {
  const [roleName, setRoleName] = useState('');
  const [selectedPages, setSelectedPages] = useState([]);
  const [obligoAccessLevel, setObligoAccessLevel] = useState('none');
  const [formIsValid, setFormIsValid] = useState(false);

  // Function to convert English field names to Hebrew display names
  const getHebrewDisplayName = (englishFieldName) => {
    return permissionsTranslations[englishFieldName] || englishFieldName;
  };

  useEffect(() => {
    setFormIsValid(roleName.trim() !== '' && selectedPages.length > 0);
  }, [roleName, selectedPages]);

  const handlePageChange = (event) => {
    const { target: { value } } = event;
    const newSelectedPages = typeof value === 'string' ? value.split(',') : value;
    
    // Check if ObligoExceptionPage or ViewObligoData is being selected/deselected
    const hadObligo = selectedPages.includes('ObligoExceptionPage') || selectedPages.includes('ViewObligoData');
    const hasObligo = newSelectedPages.includes('ObligoExceptionPage') || newSelectedPages.includes('ViewObligoData');
    
    // If obligo was removed, reset the access level
    if (hadObligo && !hasObligo) {
      setObligoAccessLevel('none');
    }
    // If obligo was just added, set default to readonly
    else if (!hadObligo && hasObligo) {
      setObligoAccessLevel('readonly');
    }
    
    setSelectedPages(newSelectedPages);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedPages.length === 0) {
      alert(translations.SELECT_AT_LEAST_ONE_PERMISSION);
      return;
    }

    if (roleName.trim() !== '') {
      // Handle obligo permissions based on access level
      let finalPages = [...selectedPages];
      
      // Remove both obligo fields first
      finalPages = finalPages.filter(p => p !== 'ObligoExceptionPage' && p !== 'ViewObligoData');
      
      // Add back based on selected access level
      const hasObligoSelection = selectedPages.includes('ObligoExceptionPage') || selectedPages.includes('ViewObligoData');
      if (hasObligoSelection) {
        if (obligoAccessLevel === 'full') {
          finalPages.push('ObligoExceptionPage');
        } else if (obligoAccessLevel === 'readonly') {
          finalPages.push('ViewObligoData');
        }
        // If 'none', don't add any obligo permission
      }
      
      onAddRole(roleName, finalPages);
    }
  };
  
  const renderPagesValue = (selected) => {
    if (selected.length === 0) {
      return <span style={PermissionsStyles.placeholderText}>{translations.SELECT_PAGES}</span>;
    }
    
    return (
      <Box sx={PermissionsStyles.chipBox}>
        {selected.map((value) => (
          <Chip 
            key={value} 
            label={getHebrewDisplayName(value)} 
            size="small"
            sx={PermissionsStyles.chip}
          />
        ))}
      </Box>
    );
  };

  const handleSelectAllPages = (event) => {
    if (event.target.checked) {
      setSelectedPages([...pages]);
    } else {
      setSelectedPages([]);
    }
  };

  return (
    <Dialog 
      open={true} 
      onClose={onCancel}
      PaperProps={{ sx: PermissionsStyles.dialogPaper }}
    >
      <DialogTitle sx={PermissionsStyles.dialogTitle}>
        <GroupAddIcon sx={PermissionsStyles.icon} />
        <Typography variant="h5" component="div" sx={PermissionsStyles.dialogTitleText}>
          {translations.ADD_NEW_ROLE}
        </Typography>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent sx={PermissionsStyles.dialogContent}>
          <Grid container spacing={3} direction="column">
            <Grid item xs={12}>
              <TextField
                required
                label={translations.ROLE_NAME}
                variant="outlined"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                fullWidth
                dir={PermissionsStyles.rtlDir}
                className={PermissionsStyles.rtlText}
                sx={PermissionsStyles.textField}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="pages-label" className={PermissionsStyles.rtlText}>
                  {translations.SELECT_PAGES}
                </InputLabel>
                <Select
                  labelId="pages-label"
                  multiple
                  value={selectedPages}
                  onChange={handlePageChange}
                  input={<OutlinedInput label={translations.SELECT_PAGES} />}
                  renderValue={renderPagesValue}
                  className={PermissionsStyles.rtlText}
                  dir={PermissionsStyles.rtlDir}
                  MenuProps={PermissionsStyles.menuProps}
                  sx={PermissionsStyles.selectMenu}
                >
                  <MenuItem value="בחר הכל" sx={PermissionsStyles.menuItemSelectAll}>
                    <Checkbox 
                      checked={pages.length > 0 && selectedPages.length === pages.length}
                      indeterminate={selectedPages.length > 0 && selectedPages.length < pages.length}
                      sx={PermissionsStyles.checkboxIndeterminate}
                      onChange={handleSelectAllPages}
                    />
                    <ListItemText primary="בחר הכל" />
                  </MenuItem>
                  {pages.map((page) => (
                    <MenuItem key={page} value={page} sx={PermissionsStyles.menuItem}>
                      <Checkbox 
                        checked={selectedPages.indexOf(page) > -1} 
                        sx={PermissionsStyles.checkbox}
                      />
                      <ListItemText primary={getHebrewDisplayName(page)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {(selectedPages.includes('ObligoExceptionPage') || selectedPages.includes('ViewObligoData')) && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="obligo-access-label" className={PermissionsStyles.rtlText}>
                    {permissionsTranslations.obligoException} - רמת גישה
                  </InputLabel>
                  <Select
                    labelId="obligo-access-label"
                    value={obligoAccessLevel}
                    onChange={(e) => setObligoAccessLevel(e.target.value)}
                    label={`${permissionsTranslations.obligoException} - רמת גישה`}
                    className={PermissionsStyles.rtlText}
                    dir={PermissionsStyles.rtlDir}
                  >
                    <MenuItem value="none">{permissionsTranslations.obligoAccessNone}</MenuItem>
                    <MenuItem value="readonly">{permissionsTranslations.obligoAccessReadonly}</MenuItem>
                    <MenuItem value="full">{permissionsTranslations.obligoAccessFull}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        
        <DialogActions sx={PermissionsStyles.dialogActions}>
          <Button
            onClick={onCancel}
            variant="outlined"
            sx={PermissionsStyles.buttonOutlined}
          >
            {translations.CANCEL}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!formIsValid}
            sx={PermissionsStyles.buttonContained}
          >
            {translations.ADD}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddRoleForm;